import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {
	Button,
	Checkbox,
	ComboBox,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	Label,
	Modal,
	MultiCombobox,
	Text,
} from '@forecast-it/design-system';
import {idFromGlobalId} from '../../../../forecast-app/shared/util/GlobalIdUtil';
import {
	AudienceTargets,
	MAX_AUDIENCE_TARGETS_PER_SUBMIT,
	NovaDeployCompanyRow,
	audienceTargetsCount,
} from './novaDeploymentData';
import {companyLabel, DeployDirectory} from './novaDeployDirectory';
import {BulkAddCandidate, NovaDeployBulkAddModal} from './NovaDeployBulkAddModal';
import {NovaVisuallyHidden} from '../shared/novaVisuallyHidden';

interface Props {
	/** The agent's own name — this modal's title, per the mockup. */
	agentName: string;
	directory: DeployDirectory;
	/** The company row being edited, or null when adding a new one. Locks the customer picker when set. */
	editingCompanyId: string | null;
	/** The row's current state, or null when adding a new company — there is nothing granted yet to diff against. */
	row: NovaDeployCompanyRow | null;
	/** Encoded ids already in the audience, so the customer picker never offers one that already has a row. */
	enabledCompanyIds: string[];
	/** A submit is in flight — every control goes inert; the state on screen does not. */
	isLoading: boolean;
	/** The directory has not arrived yet, so there is nothing yet to pick a customer or user from. */
	isDirectoryLoading: boolean;
	/**
	 * Called with the full difference between what Save would leave in place and what the row already
	 * held, as one pair — never as two separate calls. Switching a company from a wholesale grant to
	 * named users is a remove of the grant and an add of the people at once; two calls would leave the
	 * grant removed and nobody added if the second one failed.
	 */
	onSubmit: (add: AudienceTargets, remove: AudienceTargets) => void;
	onClose: () => void;
}

const EMPTY_TARGETS: AudienceTargets = {companyIds: [], persons: []};

/**
 * The per-company audience editor: one customer, whether it holds a wholesale grant, and which of its
 * people are named individually — the mockup's `SELECT CUSTOMER` / `SELECT ALL USERS` / `SELECT USERS`
 * modal, opened from the `+` control and from a row's edit pencil alike.
 *
 * Save diffs rather than replaces: it compares what is on screen against `row`'s own state and sends
 * only the difference, as one `{add, remove}` submit — never as two separate calls, which is the whole
 * reason the underlying write carries both sides at once (see `onSubmit`'s own comment).
 */
export const NovaDeployAudienceModal = ({
	agentName,
	directory,
	editingCompanyId,
	row,
	enabledCompanyIds,
	isLoading,
	isDirectoryLoading,
	onSubmit,
	onClose,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();

	// Only relevant while adding: the customer chosen from the picker below. Editing an existing row
	// never changes which company this modal is about, so `editingCompanyId` always wins over it.
	const [pickedCompanyId, setPickedCompanyId] = useState<string | null>(null);
	const companyId = editingCompanyId ?? pickedCompanyId;

	const originalHasCompanyGrant = row?.hasCompanyGrant ?? false;
	const originalPersonIds = row?.personIds ?? [];

	const [selectAllChecked, setSelectAllChecked] = useState<boolean>(originalHasCompanyGrant);
	const [finalPersonIds, setFinalPersonIds] = useState<string[]>(originalPersonIds);
	// Companies queued through "Bulk add companies" — a wholesale grant each, independent of whichever
	// single customer is picked above (see the bulk-add control's own placement in the mockup).
	const [bulkCompanyIds, setBulkCompanyIds] = useState<string[]>([]);

	// Selections belong to the one company they were made for. If the picked customer changes (only
	// possible while adding — editing locks it), a person or a company-grant tick made under the
	// previous customer would otherwise be counted and submitted for the wrong company. Derived during
	// render, the same pattern `NovaDeployAudienceModal`'s previous design used and for the same
	// reason: it avoids an effect that writes state on render, which is how a re-render loop starts.
	const lastCompanyIdRef = useRef(companyId);
	if (lastCompanyIdRef.current !== companyId) {
		lastCompanyIdRef.current = companyId;
		setSelectAllChecked(false);
		setFinalPersonIds([]);
	}

	const customerOptions = editingCompanyId
		? directory.companies
		: directory.companies.filter(company => !enabledCompanyIds.includes(company.companyId));

	const companyPeople = companyId ? directory.personsByCompanyId.get(companyId) ?? [] : [];
	const addablePeople = companyPeople.filter(person => !finalPersonIds.includes(person.personId));
	const queuedPeople = companyPeople.filter(person => finalPersonIds.includes(person.personId));

	const add: AudienceTargets = {
		// De-duplicated on top of the exclusion below: the picked company is the one id that filter
		// cannot see coming from the bulk-add candidates themselves (it is not yet in `enabledCompanyIds`
		// until Save lands), so this is the last line of defence against submitting it twice.
		companyIds: Array.from(
			new Set([...(companyId && selectAllChecked && !originalHasCompanyGrant ? [companyId] : []), ...bulkCompanyIds])
		),
		persons: companyId
			? finalPersonIds.filter(personId => !originalPersonIds.includes(personId)).map(personId => ({personId, companyId}))
			: [],
	};
	const remove: AudienceTargets = {
		companyIds: companyId && !selectAllChecked && originalHasCompanyGrant ? [companyId] : [],
		persons: companyId
			? originalPersonIds.filter(personId => !finalPersonIds.includes(personId)).map(personId => ({personId, companyId}))
			: [],
	};

	const changeCount = audienceTargetsCount(add) + audienceTargetsCount(remove);
	const isOverCap = changeCount > MAX_AUDIENCE_TARGETS_PER_SUBMIT;
	const canSave = changeCount > 0 && !isOverCap && !isLoading;
	const controlsDisabled = isLoading || isDirectoryLoading;

	const bulkCompanyCandidates: BulkAddCandidate[] = directory.companies
		.filter(
			company =>
				company.companyId !== companyId &&
				!enabledCompanyIds.includes(company.companyId) &&
				!bulkCompanyIds.includes(company.companyId)
		)
		.map(company => ({id: company.companyId, numericId: company.numericId}));

	// A person carries no numeric id of its own in this directory — only its encoded global id — so
	// the same numeric-paste convention companies use is recovered here by decoding it, rather than
	// giving persons a second identifier only this control would ever read.
	const bulkPersonCandidates: BulkAddCandidate[] = addablePeople.map(person => ({
		id: person.personId,
		numericId: idFromGlobalId(person.personId),
	}));

	// What "remove feature access" would submit — the row's entire original grant, independent of
	// whatever is queued on screen. Capped the same way the Save path caps `add`/`remove`: one submit is
	// one transaction, so a company with more than the cap's worth of named users must not reach the
	// service only to be refused there.
	const removeAccessTargets: AudienceTargets = {
		companyIds: companyId && originalHasCompanyGrant ? [companyId] : [],
		persons: companyId ? originalPersonIds.map(personId => ({personId, companyId})) : [],
	};
	const isRemoveAccessOverCap = audienceTargetsCount(removeAccessTargets) > MAX_AUDIENCE_TARGETS_PER_SUBMIT;

	const handleRemoveAccess = (): void => {
		if (!companyId || isRemoveAccessOverCap) {
			return;
		}
		onSubmit(EMPTY_TARGETS, removeAccessTargets);
	};

	return (
		<Modal onCloseModal={onClose}>
			<Modal.Title>{agentName}</Modal.Title>
			{/* The hook sits on Content, not on Modal: the Modal root does not forward data-* attributes,
			    so a data-cy there renders nothing at all. */}
			<Modal.Content data-cy="nova-deploy-audience-modal">
				<FlexColumn gap={'l'}>
					{isDirectoryLoading ? (
						// Swapped in for the whole customer section rather than merely greying it out: the
						// directory fetch starts the same tick this modal opens, so an author's very first sight
						// of it would otherwise be an empty, disabled picker indistinguishable from "no companies
						// left to add" — a real, different state this one must not be confused with.
						<Text data-cy="nova-deploy-audience-loading">
							{formatMessage({id: 'nova.deploy.audience.loadingCompanies'})}
						</Text>
					) : (
						<FlexColumn gap={'s'}>
							{/* `value`/`onChange`, not `selectedKey`/`onSelectionChange`: this component assigns its
							    own `value`/`onChange` over those two before handing the rest to React-Aria, so a
							    handler passed the advertised way is silently discarded. Both `disabled` and
							    `isDisabled` are passed for the locked case: `disabled` alone only greys the input,
							    `isDisabled` is what actually reaches React-Aria and refuses a change. */}
							<ComboBox
								data-cy="nova-deploy-audience-customer"
								label={formatMessage({id: 'nova.deploy.audience.selectCustomer'})}
								width={ComboBox.WIDTH.FLEX}
								items={customerOptions.map(company => ({id: company.companyId, name: companyLabel(company)}))}
								value={companyId}
								disabled={!!editingCompanyId || controlsDisabled}
								isDisabled={!!editingCompanyId || controlsDisabled}
								onChange={key => setPickedCompanyId(key ? String(key) : null)}
							>
								{(company: {id: string; name: string}) => (
									<ComboBox.Item key={company.id}>{company.name}</ComboBox.Item>
								)}
							</ComboBox>
							{/* Right-aligned under the picker it extends, as the mockup has it — a FlexRow rather
							    than a margin, because the button sizes itself and there is nothing to push
							    against otherwise.

							    Disabled while editing an existing row too: a modal titled for one company must
							    not become a back door to granting fifty unrelated ones wholesale. */}
							<FlexRow justifyContent={'end'}>
								<NovaDeployBulkAddModal
									dataCy="nova-deploy-audience-bulk-companies"
									label={formatMessage({id: 'nova.deploy.audience.bulkAddCompanies'})}
									candidates={bulkCompanyCandidates}
									disabled={!!editingCompanyId || controlsDisabled}
									onApply={ids => setBulkCompanyIds(current => Array.from(new Set([...current, ...ids])))}
								/>
							</FlexRow>
						</FlexColumn>
					)}

					<Checkbox
						data-cy="nova-deploy-audience-select-all"
						checked={selectAllChecked}
						disabled={!companyId || controlsDisabled}
						onChange={() => setSelectAllChecked(current => !current)}
						labelText={formatMessage({id: 'nova.deploy.audience.selectAllUsers'})}
					/>

					<FlexColumn gap={'s'}>
						{/* Both this picker and the bulk paste below it are shut while "select all users" is
						    ticked: the whole company is already granted, so naming individuals inside it
						    adds nothing and would queue people the diff then has to discard.

						    Gated by an empty `items` rather than by `disabled` alone: `MultiCombobox`'s own
						    `disabled` only styles the control, with no real equivalent that stops a pick — an
						    empty `items` is the only thing that actually does. `selectedKeys` stays empty on
						    purpose: a picked person leaves `addablePeople` (see below) the moment they are
						    queued, so nothing offered here is ever already selected. */}
						<MultiCombobox
							data-cy="nova-deploy-audience-people"
							label={formatMessage({id: 'nova.deploy.audience.selectUsers'})}
							width={MultiCombobox.WIDTH.FLEX}
							disabled={!companyId || selectAllChecked || controlsDisabled}
							items={
								companyId && !selectAllChecked && !controlsDisabled
									? addablePeople.map(person => ({key: person.personId, textValue: person.label}))
									: []
							}
							selectedKeys={[]}
							setSelectedKeys={next => setFinalPersonIds(current => Array.from(new Set([...current, ...next])))}
						>
							{item => <span data-cy={`nova-deploy-audience-person-${item.key}`}>{item.textValue}</span>}
						</MultiCombobox>
					</FlexColumn>

					<FlexColumn gap={'s'}>
						<Label>{formatMessage({id: 'nova.deploy.audience.usersThatWillBeAdded'})}</Label>
						{queuedPeople.map(person => (
							<FlexRow key={person.personId} gap={'s'} justifyContent="space-between" alignItems="center">
								<Text data-cy={`nova-deploy-audience-queued-${person.personId}`}>{person.label}</Text>
								<NovaVisuallyHidden id={`nova-deploy-audience-drop-label-${person.personId}`}>
									{formatMessage({id: 'common.remove'})}
								</NovaVisuallyHidden>
								<Button
									data-cy={`nova-deploy-audience-drop-${person.personId}`}
									emphasis={EMPHASIS.LOW}
									icon="trash"
									aria-labelledby={`nova-deploy-audience-drop-label-${person.personId}`}
									disabled={controlsDisabled}
									onPress={() =>
										setFinalPersonIds(current => current.filter(personId => personId !== person.personId))
									}
								/>
							</FlexRow>
						))}
						<FlexRow justifyContent={'end'}>
							<NovaDeployBulkAddModal
								dataCy="nova-deploy-audience-bulk-users"
								label={formatMessage({id: 'nova.deploy.audience.bulkAddUsers'})}
								candidates={bulkPersonCandidates}
								disabled={!companyId || selectAllChecked || controlsDisabled}
								onApply={ids => setFinalPersonIds(current => Array.from(new Set([...current, ...ids])))}
							/>
						</FlexRow>
					</FlexColumn>

					{/* The cap warning lives in the content, not the footer: the Modal's footer renders only
					    its own footer buttons and drops anything else, so it would silently disappear
					    there. It is the only thing said about the cap — a running count of what is
					    selected is already visible in the pickers themselves. */}
					{isOverCap ? (
						// One submit is one transaction, so the cap is real rather than advisory: telling the
						// author here beats a rejected request after they finished choosing.
						<Text data-cy="nova-deploy-audience-cap-warning">
							{formatMessage({id: 'nova.deploy.audience.capWarning'}, {max: MAX_AUDIENCE_TARGETS_PER_SUBMIT})}
						</Text>
					) : null}
				</FlexColumn>
			</Modal.Content>
			{/* Footer buttons take `dataCy`, not `data-cy` — the Modal forwards it on. `REMOVE FEATURE
			    ACCESS` bypasses whatever is queued on screen entirely and targets the row's own original
			    state directly — see its own handler — which is why it is disabled while adding a company
			    that has nothing granted yet to remove, and while removing what is there would itself
			    exceed the per-submit cap. */}
			{/* The destructive action sits on the left, away from the two it must not be mistaken for.
			    Which footer subcomponent each button uses is what positions it, not its meaning: the
			    footer renders its children in order and wraps only the TERTIARY one in a growing,
			    right-aligned box. So a tertiary in the MIDDLE pushes everything after it to the right
			    and leaves everything before it on the left — Remove, gap, Cancel, Save. Tertiary first
			    or last cannot express this, which is why the arrangement looks upside down. */}
			<Modal.Footer>
				<Modal.SecondaryFooterButton
					dataCy="nova-deploy-audience-remove-access"
					disabled={row === null || isLoading || isRemoveAccessOverCap}
					onPress={handleRemoveAccess}
				>
					{formatMessage({id: 'nova.deploy.audience.removeFeatureAccess'})}
				</Modal.SecondaryFooterButton>
				<Modal.TertiaryFooterButton dataCy="nova-deploy-audience-cancel" onPress={onClose}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.TertiaryFooterButton>
				<Modal.PrimaryFooterButton
					dataCy="nova-deploy-audience-save"
					disabled={!canSave}
					onPress={() => onSubmit(add, remove)}
				>
					{formatMessage({id: 'nova.deploy.audience.save'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
